import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSecurityContext } from '@/contexts/MaintenanceContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ArrowLeft, Edit, Settings, Phone, Mail, MapPin, Building, CreditCard, FileText, FolderOpen, ExternalLink, MoreVertical, ClipboardList, Briefcase, FileSignature } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { MandatoSEPATab } from '@/components/MandatoSEPATab';
import { DocumentosTab } from '@/components/DocumentosTab';
import { PartesClienteTab } from '@/components/cliente/PartesClienteTab';
import { ChecklistsClienteTab } from '@/components/cliente/ChecklistsClienteTab';
import { InformesClienteTab } from '@/components/cliente/InformesClienteTab';

import { ObrasClienteTab } from '@/components/cliente/ObrasClienteTab';
import { SedesClienteTab } from '@/components/cliente/SedesClienteTab';
import { ContratosClienteTab } from '@/components/cliente/ContratosClienteTab';
import { CertificadosClienteTab } from '@/components/cliente/CertificadosClienteTab';
import { PortalClienteTab } from '@/components/cliente/PortalClienteTab';
import { HoldedClienteSyncBadge } from '@/components/clientes/HoldedSyncControls';

const DetalleCliente: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { clientes, setClienteActual, editarCliente } = useSecurityContext();
  const { perfil } = useAuth();

  const [dialogoEdicion, setDialogoEdicion] = useState(false);
  const [datosEdicion, setDatosEdicion] = useState({
    // Básicos
    nombre: '',
    direccion: '',
    piso: '',
    puerta: '',
    poblacion: '',
    codigo_postal: '',
    telefono: '',
    email: '',
    // Facturación
    nif_cif: '',
    nombre_fiscal: '',
    direccion_fiscal: '',
    telefono_facturacion: '',
    email_facturacion: '',
  });

  const cliente = clientes.find(c => c.id === id);

  React.useEffect(() => {
    if (cliente) {
      setClienteActual(cliente);
      setDatosEdicion({
        nombre: cliente.nombre || '',
        direccion: cliente.direccion || '',
        piso: cliente.piso || '',
        puerta: cliente.puerta || '',
        poblacion: cliente.poblacion || '',
        codigo_postal: cliente.codigo_postal || '',
        telefono: cliente.telefono || '',
        email: cliente.email || '',
        nif_cif: cliente.nif_cif || cliente.dni_nif || '',
        nombre_fiscal: cliente.nombre_fiscal || '',
        direccion_fiscal: cliente.direccion_fiscal || '',
        telefono_facturacion: cliente.telefono_facturacion || '',
        email_facturacion: cliente.email_facturacion || '',
      });
    }
  }, [cliente, setClienteActual]);

  if (!cliente) {
    return (
      <div className="text-center py-8">
        <p className="text-muted-foreground">Cliente no encontrado</p>
        <Button onClick={() => navigate('/clientes')} className="mt-4">Volver a Clientes</Button>
      </div>
    );
  }

  const handleSubmitEdicion = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      // NIF unificado -> escribir en ambos campos por compatibilidad
      const payload = {
        ...datosEdicion,
        dni_nif: datosEdicion.nif_cif || null,
      };
      await editarCliente(cliente.id, payload);
      setDialogoEdicion(false);
      toast.success('Cliente actualizado correctamente');
    } catch {
      toast.error('Error al actualizar el cliente');
    }
  };

  const handleVerSistemas = () => navigate('/sistemas-cliente');

  const renderEditForm = (suffix: string) => (
    <form onSubmit={handleSubmitEdicion} className="space-y-4">
      <Tabs defaultValue="basicos" className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="basicos">Datos básicos</TabsTrigger>
          <TabsTrigger value="facturacion">Facturación</TabsTrigger>
        </TabsList>

        <TabsContent value="basicos" className="space-y-4 pt-2">
          <div>
            <Label htmlFor={`nombre-${suffix}`}>Nombre comercial *</Label>
            <Input id={`nombre-${suffix}`} value={datosEdicion.nombre} onChange={(e) => setDatosEdicion({ ...datosEdicion, nombre: e.target.value })} required />
          </div>
          <div>
            <Label htmlFor={`direccion-${suffix}`}>Dirección principal</Label>
            <Input id={`direccion-${suffix}`} value={datosEdicion.direccion} onChange={(e) => setDatosEdicion({ ...datosEdicion, direccion: e.target.value })} placeholder="Calle y número" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor={`piso-${suffix}`}>Piso</Label>
              <Input id={`piso-${suffix}`} value={datosEdicion.piso} onChange={(e) => setDatosEdicion({ ...datosEdicion, piso: e.target.value })} />
            </div>
            <div>
              <Label htmlFor={`puerta-${suffix}`}>Puerta</Label>
              <Input id={`puerta-${suffix}`} value={datosEdicion.puerta} onChange={(e) => setDatosEdicion({ ...datosEdicion, puerta: e.target.value })} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor={`cp-${suffix}`}>Código postal</Label>
              <Input id={`cp-${suffix}`} value={datosEdicion.codigo_postal} onChange={(e) => setDatosEdicion({ ...datosEdicion, codigo_postal: e.target.value })} />
            </div>
            <div>
              <Label htmlFor={`pob-${suffix}`}>Población</Label>
              <Input id={`pob-${suffix}`} value={datosEdicion.poblacion} onChange={(e) => setDatosEdicion({ ...datosEdicion, poblacion: e.target.value })} />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label htmlFor={`tel-${suffix}`}>Teléfono</Label>
              <Input id={`tel-${suffix}`} value={datosEdicion.telefono} onChange={(e) => setDatosEdicion({ ...datosEdicion, telefono: e.target.value })} />
            </div>
            <div>
              <Label htmlFor={`email-${suffix}`}>Email</Label>
              <Input id={`email-${suffix}`} type="email" value={datosEdicion.email} onChange={(e) => setDatosEdicion({ ...datosEdicion, email: e.target.value })} />
            </div>
          </div>
        </TabsContent>

        <TabsContent value="facturacion" className="space-y-4 pt-2">
          <div>
            <Label htmlFor={`nif-${suffix}`}>NIF / CIF *</Label>
            <Input id={`nif-${suffix}`} value={datosEdicion.nif_cif} onChange={(e) => setDatosEdicion({ ...datosEdicion, nif_cif: e.target.value.toUpperCase() })} className="font-mono uppercase" />
          </div>
          <div>
            <Label htmlFor={`nomfis-${suffix}`}>Nombre fiscal</Label>
            <Input id={`nomfis-${suffix}`} value={datosEdicion.nombre_fiscal} onChange={(e) => setDatosEdicion({ ...datosEdicion, nombre_fiscal: e.target.value })} />
          </div>
          <div>
            <Label htmlFor={`dirfis-${suffix}`}>Dirección de facturación</Label>
            <Textarea id={`dirfis-${suffix}`} value={datosEdicion.direccion_fiscal} onChange={(e) => setDatosEdicion({ ...datosEdicion, direccion_fiscal: e.target.value })} rows={2} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label htmlFor={`telfac-${suffix}`}>Teléfono facturación</Label>
              <Input id={`telfac-${suffix}`} value={datosEdicion.telefono_facturacion} onChange={(e) => setDatosEdicion({ ...datosEdicion, telefono_facturacion: e.target.value })} />
            </div>
            <div>
              <Label htmlFor={`emailfac-${suffix}`}>Email facturación</Label>
              <Input id={`emailfac-${suffix}`} type="email" value={datosEdicion.email_facturacion} onChange={(e) => setDatosEdicion({ ...datosEdicion, email_facturacion: e.target.value })} />
            </div>
          </div>
        </TabsContent>
      </Tabs>

      <p className="text-xs text-muted-foreground border-t pt-3">
        Los datos de abonado, contraseñas CRA y contactos autorizados se gestionan ahora en cada <strong>sede</strong> del cliente.
      </p>

      <div className="flex justify-end gap-2 pt-2">
        <Button type="button" variant="outline" onClick={() => setDialogoEdicion(false)}>Cancelar</Button>
        <Button type="submit">Guardar cambios</Button>
      </div>
    </form>
  );

  return (
    <div className="container mx-auto py-4 sm:py-6 space-y-4 sm:space-y-6 px-3 sm:px-6">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 sm:gap-4 min-w-0">
          <Button variant="outline" size="sm" onClick={() => navigate('/clientes')} className="shrink-0">
            <ArrowLeft className="h-4 w-4 sm:mr-2" />
            <span className="hidden sm:inline">Volver a Clientes</span>
          </Button>
          <h1 className="text-xl sm:text-3xl font-bold truncate">{cliente.nombre}</h1>
          <HoldedClienteSyncBadge cliente={cliente} onSynced={() => window.location.reload()} />
        </div>

        {/* Mobile: dropdown menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" className="md:hidden shrink-0 min-h-[48px] min-w-[48px]">
              <MoreVertical className="h-6 w-6" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="bg-popover min-w-[200px]">
            <DropdownMenuItem onClick={handleVerSistemas} className="py-3 text-base">
              <Settings className="h-5 w-5 mr-2" /> Ver Sistemas
            </DropdownMenuItem>
            {perfil?.rol === "admin" && (
              <DropdownMenuItem onClick={() => setDialogoEdicion(true)} className="py-3 text-base">
                <Edit className="h-5 w-5 mr-2" /> Editar datos
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Desktop: buttons */}
        <div className="hidden md:flex gap-2 shrink-0">
          {perfil?.rol === "admin" && (
            <Button variant="outline" onClick={() => setDialogoEdicion(true)}>
              <Edit className="h-4 w-4 mr-2" /> Editar datos
            </Button>
          )}
          <Button onClick={handleVerSistemas}>
            <Settings className="h-4 w-4 mr-2" /> Ver Sistemas
          </Button>
        </div>
      </div>

      {/* Shared edit dialog (desktop + mobile) */}
      {perfil?.rol === "admin" && (
        <Dialog open={dialogoEdicion} onOpenChange={setDialogoEdicion}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Editar cliente</DialogTitle>
            </DialogHeader>
            {renderEditForm('edicion')}
          </DialogContent>
        </Dialog>
      )}

      <Tabs defaultValue="basicos" className="w-full">
        <TabsList className="flex w-full overflow-x-auto h-auto flex-wrap gap-1 bg-muted/50 p-1" style={{ scrollbarWidth: 'none' }}>
          <TabsTrigger value="basicos" className="flex-shrink-0 text-xs sm:text-sm">Datos básicos</TabsTrigger>
          {perfil?.rol === "admin" && <TabsTrigger value="fiscal" className="flex-shrink-0 text-xs sm:text-sm">Facturación</TabsTrigger>}
          <TabsTrigger value="sedes" className="flex-shrink-0 text-xs sm:text-sm">
            <Building className="h-3.5 w-3.5 mr-1" /> Sedes
          </TabsTrigger>
          <TabsTrigger value="obras" className="flex-shrink-0 text-xs sm:text-sm">
            <Briefcase className="h-3.5 w-3.5 mr-1" /> Obras
          </TabsTrigger>
          <TabsTrigger value="partes" className="flex-shrink-0 text-xs sm:text-sm">
            <FileText className="h-3.5 w-3.5 mr-1" /> Partes
          </TabsTrigger>
          <TabsTrigger value="checklists" className="flex-shrink-0 text-xs sm:text-sm">
            <ClipboardList className="h-3.5 w-3.5 mr-1" /> Checklists
          </TabsTrigger>
          <TabsTrigger value="informes" className="flex-shrink-0 text-xs sm:text-sm">
            <FileText className="h-3.5 w-3.5 mr-1" /> Informes
          </TabsTrigger>
          {(perfil?.rol === "admin" || perfil?.rol === "gestor") && (
            <TabsTrigger value="contratos" className="flex-shrink-0 text-xs sm:text-sm">
              <FileSignature className="h-3.5 w-3.5 mr-1" /> Contratos
            </TabsTrigger>
          )}
          {(perfil?.rol === "admin" || perfil?.rol === "gestor") && (
            <TabsTrigger value="certificados" className="flex-shrink-0 text-xs sm:text-sm">
              <FileSignature className="h-3.5 w-3.5 mr-1" /> Certificados
            </TabsTrigger>
          )}

          {perfil?.rol === "admin" && (
            <TabsTrigger value="mandato-sepa" className="flex-shrink-0 text-xs sm:text-sm">
              <CreditCard className="h-3.5 w-3.5 mr-1" /> SEPA
            </TabsTrigger>
          )}
          <TabsTrigger value="documentos" className="flex-shrink-0 text-xs sm:text-sm">
            <FolderOpen className="h-3.5 w-3.5 mr-1" /> Docs
          </TabsTrigger>
          {(perfil?.rol === "admin" || perfil?.rol === "gestor") && (
            <TabsTrigger value="portal" className="flex-shrink-0 text-xs sm:text-sm">
              <ExternalLink className="h-3.5 w-3.5 mr-1" /> Portal
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="basicos">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building className="h-5 w-5" /> Información básica
              </CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center gap-3">
                <Building className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">Nombre</p>
                  <p className="font-medium">{cliente.nombre}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Phone className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">Teléfono</p>
                  <p className="font-medium">{cliente.telefono || 'No especificado'}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Mail className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">Email</p>
                  <p className="font-medium">{cliente.email || 'No especificado'}</p>
                </div>
              </div>
              <div className="flex items-start gap-3 md:col-span-2">
                <MapPin className="h-4 w-4 text-muted-foreground mt-1" />
                <div className="flex-1">
                  <p className="text-sm text-muted-foreground">Dirección principal</p>
                  <p className="font-medium">
                    {cliente.direccion || 'No especificado'}
                    {(cliente.piso || cliente.puerta) && (
                      <span className="text-muted-foreground"> — Piso {cliente.piso || '-'}, Puerta {cliente.puerta || '-'}</span>
                    )}
                  </p>
                  {(cliente.poblacion || cliente.codigo_postal) && (
                    <p className="text-sm text-muted-foreground">{[cliente.codigo_postal, cliente.poblacion].filter(Boolean).join(' ')}</p>
                  )}
                  {cliente.direccion && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="mt-2 gap-2"
                      onClick={() => window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent([cliente.direccion, cliente.codigo_postal, cliente.poblacion].filter(Boolean).join(", "))}`, "_blank")}
                    >
                      <ExternalLink className="h-4 w-4" /> Abrir en Google Maps
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {perfil?.rol === "admin" && (
          <TabsContent value="fiscal">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CreditCard className="h-5 w-5" /> Datos de facturación
                </CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">NIF / CIF</p>
                  <p className="font-medium font-mono">{cliente.nif_cif || cliente.dni_nif || 'No especificado'}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Nombre fiscal</p>
                  <p className="font-medium">{cliente.nombre_fiscal || 'No especificado'}</p>
                </div>
                <div className="md:col-span-2">
                  <p className="text-sm text-muted-foreground">Dirección de facturación</p>
                  <p className="font-medium whitespace-pre-line">{cliente.direccion_fiscal || 'No especificado'}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Teléfono facturación</p>
                  <p className="font-medium">{cliente.telefono_facturacion || 'No especificado'}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Email facturación</p>
                  <p className="font-medium">{cliente.email_facturacion || 'No especificado'}</p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        )}

        <TabsContent value="sedes">
          <SedesClienteTab clienteId={cliente.id} />
        </TabsContent>

        <TabsContent value="obras">
          <ObrasClienteTab clienteId={cliente.id} clienteNombre={cliente.nombre} />
        </TabsContent>

        <TabsContent value="partes">
          <PartesClienteTab clienteId={cliente.id} clienteNombre={cliente.nombre} />
        </TabsContent>

        <TabsContent value="checklists">
          <ChecklistsClienteTab clienteId={cliente.id} clienteNombre={cliente.nombre} />
        </TabsContent>

        <TabsContent value="informes">
          <InformesClienteTab clienteId={cliente.id} clienteNombre={cliente.nombre} />
        </TabsContent>


        {(perfil?.rol === "admin" || perfil?.rol === "gestor") && (
          <TabsContent value="contratos">
            <ContratosClienteTab clienteId={cliente.id} />
          </TabsContent>
        )}

        {(perfil?.rol === "admin" || perfil?.rol === "gestor") && (
          <TabsContent value="certificados">
            <CertificadosClienteTab clienteId={cliente.id} />
          </TabsContent>
        )}

        {perfil?.rol === "admin" && (
          <TabsContent value="mandato-sepa">
            <MandatoSEPATab clienteId={cliente.id} clienteNombre={cliente.nombre} />
          </TabsContent>
        )}

        <TabsContent value="documentos">
          <DocumentosTab clienteId={cliente.id} clienteNombre={cliente.nombre} />
        </TabsContent>

        {(perfil?.rol === "admin" || perfil?.rol === "gestor") && (
          <TabsContent value="portal">
            <PortalClienteTab clienteId={cliente.id} />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
};

export default DetalleCliente;
